import { useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { handleError } from '../utils/lib';
import AgendaDialogs from '../components/agenda/AgendaDialogs';

/**
 * Hook personalizado para manejar acciones comunes de agenda
 * @param {Function} onSuccess - Función a ejecutar después de completar con éxito (para recargar datos, etc.)
 * @param {Function} showSnackbar - Función para mostrar notificaciones
 * @returns {Object} - Estado y controladores para las acciones de agenda
 */
const useAgendaActions = (onSuccess, showSnackbar) => {
  // Estado para el diálogo de posponer
  const [postponeDialog, setPostponeDialog] = useState({
    open: false,
    selectedDate: null,
    item: null,
    isLoading: false
  });
  
  // Estado para el diálogo de completar
  const [completeDialog, setCompleteDialog] = useState({
    open: false,
    item: null,
    isLoading: false
  });

  // Abrir diálogo para posponer
  const handleReschedule = (agenda) => {
    setPostponeDialog({
      open: true,
      selectedDate: dayjs().add(1, 'day'),
      item: agenda,
      isLoading: false
    });
  };
  
  // Cerrar diálogo de posponer
  const handlePostponeClose = () => {
    setPostponeDialog({
      open: false,
      selectedDate: null,
      item: null,
      isLoading: false
    });
  };

  // Actualizar la fecha seleccionada
  const handleDateChange = (newDate) => {
    setPostponeDialog(prev => ({
      ...prev,
      selectedDate: newDate
    }));
  };
  
  // Confirmar posposición
  const handlePostponeConfirm = async () => {
    // Activar estado de loading
    setPostponeDialog(prev => ({
      ...prev,
      isLoading: true
    }));

    try {
      const { item, selectedDate } = postponeDialog;
      
      // Mantener la hora original del item
      const originalTime = dayjs(item.begin_time);
      const newDateTime = selectedDate
        .hour(originalTime.hour())
        .minute(originalTime.minute())
        .second(originalTime.second());

      await axios.put(
        `${import.meta.env.VITE_API_URL}agenda?id=${item.id}&reschedule&begin_time=${newDateTime.format('YYYY-MM-DD HH:mm:ss')}`,
        {},
        { withCredentials: true }
      );

      // Cerrar diálogo
      handlePostponeClose();
      
      // Ejecutar callback de éxito (recargar datos)
      if (onSuccess) onSuccess();
      
      // Mostrar notificación
      //if (showSnackbar) showSnackbar("Evento reprogramado con éxito");
    } catch (error) {
      // Desactivar loading en caso de error
      setPostponeDialog(prev => ({
        ...prev,
        isLoading: false
      }));
      handleError(error, showSnackbar);
    }
  };
  
  // Abrir diálogo para completar
  const handleComplete = (agenda) => {
    setCompleteDialog({
      open: true,
      item: agenda,
      isLoading: false
    });
  };
  
  // Cerrar diálogo de completar
  const handleCompleteClose = () => {
    setCompleteDialog({
      open: false,
      item: null,
      isLoading: false
    });
  };
  
  // Confirmar completado
  const handleCompleteConfirm = async () => {
    // Activar estado de loading
    setCompleteDialog(prev => ({
      ...prev,
      isLoading: true
    }));

    try {
      const { item } = completeDialog;

      await axios.put(
        `${import.meta.env.VITE_API_URL}agenda?id=${item.id}&set_executed`,
        {},
        { withCredentials: true }
      );

      // Cerrar diálogo
      handleCompleteClose();
      
      // Ejecutar callback de éxito (recargar datos)
      if (onSuccess) onSuccess();
      
      // Mostrar notificación
      //if (showSnackbar) showSnackbar("Evento marcado como completado");
    } catch (error) {
      // Desactivar loading en caso de error
      setCompleteDialog(prev => ({
        ...prev,
        isLoading: false
      }));
      handleError(error, showSnackbar);
    }
  };

  // Datos y funciones que exponemos
  return {
    // Estados para los diálogos
    postponeDialog,
    completeDialog,
    
    // Funciones para controlar los diálogos
    handleReschedule,
    handlePostponeClose,
    handlePostponeConfirm,
    handleComplete,
    handleCompleteClose,
    handleCompleteConfirm,
    handleDateChange,
    
    // Componente de diálogos renderizado
    AgendaDialogs: () => (
      <AgendaDialogs
        postponeDialog={postponeDialog}
        completeDialog={completeDialog}
        handlePostponeClose={handlePostponeClose}
        handlePostponeConfirm={handlePostponeConfirm}
        handleCompleteClose={handleCompleteClose}
        handleCompleteConfirm={handleCompleteConfirm}
        handleDateChange={handleDateChange}
      />
    )
  };
};

export default useAgendaActions;
